import { cloneDeep } from 'lodash-es';
import moment from 'moment';

import { IListTableColumn, IMeasurementTableRowData } from '~/src/dash/components/list-table';
import { IListItem } from '~/src/dash/types/menuList';
import { ICalendarQuickSelectorOptions } from '~/src/dash/utils/calendar';
import {
  DateGroup,
  DateGroupDays,
  DateGroupHours,
  DateGroupMonths,
  DateGroupWeeks,
  getStartDate,
} from '~/src/dash/utils/date';
import { IIASFirstAndThirdPartyCompareData } from '~/src/dash/views/common/ias-data-types';
import {
  IMeasurementFilter,
  IMeasurementTableColumn,
  IMeasurementTableGroupBy,
} from '~/src/dash/views/measurement/config';
import { ISingleSelectOption } from '~/src/dash/views/singleselect-view';

export interface IMeasurementDataItem extends Partial<IIASFirstAndThirdPartyCompareData> {
  Date: moment.Moment | string;
  CDate: moment.Moment | string;
  DateLabel: string;
  Time: string;
}

export const blankMeasurementDataItem: IMeasurementDataItem = {
  Date: moment(),
  DateLabel: 'Day 1',
  CDate: moment(),
  Time: '',
};

export const assignDateLabel = (
  currentDataItem: IMeasurementDataItem,
  i: number,
  densityRange: DateGroup,
): IMeasurementDataItem => {
  if (densityRange === DateGroupHours) {
    currentDataItem.DateLabel = 'Hour ' + (i + 1).toString();
  }
  if (densityRange === DateGroupDays) {
    currentDataItem.DateLabel = 'Day ' + (i + 1).toString();
  }
  if (densityRange === DateGroupWeeks) {
    currentDataItem.DateLabel = 'Week ' + (i + 1).toString();
  }
  if (densityRange === DateGroupMonths) {
    currentDataItem.DateLabel = 'Month ' + (i + 1).toString();
  }
  return currentDataItem;
};

export const dateMatches = (
  densityRange: string,
  date1: moment.Moment,
  date2: moment.Moment,
): boolean => {
  if (densityRange === DateGroupHours) {
    return date1.format('HH:mm MMM D') === date2.format('HH:mm MMM D');
  } else if (densityRange === DateGroupDays) {
    return date1.format('MMM D') === date2.format('MMM D');
  } else if (densityRange === DateGroupWeeks) {
    return date1.week() === date2.week();
  } else if (densityRange === DateGroupMonths) {
    return date1.month() === date2.month();
  }
  return false;
};

export interface ICalendarProps {
  rangeSelected: string;
  rangeDisplaySelected: string;
  startDate: moment.Moment;
  endDate: moment.Moment;
  displayStartDate: moment.Moment;
  displayEndDate: moment.Moment;
}

const dateRangeStrings = {
  YESTERDAY: 'Yesterday',
  THIS_WEEK_SUN_YESTERDAY: 'This Week(Sun-Yesterday)',
  THIS_WEEK_SUN_TODAY: 'This Week(Sun-Today)',
  LAST_7_DAYS: 'Last 7 Days',
  LAST_WEEK: 'Last Week(Sun-Sat)',
  THIS_MONTH: 'This Month',
  LAST_30_DAYS: 'Last 30 Days',
  LAST_MONTH: 'Last Month',
};

const {
  YESTERDAY,
  LAST_7_DAYS,
  THIS_WEEK_SUN_YESTERDAY,
  THIS_WEEK_SUN_TODAY,
  LAST_WEEK,
  THIS_MONTH,
  LAST_30_DAYS,
  LAST_MONTH,
} = dateRangeStrings;

export const generateCalendarProps = (timezone: string): ICalendarProps => {
  return {
    rangeSelected: LAST_7_DAYS,
    rangeDisplaySelected: LAST_7_DAYS,
    startDate: getStartDate(timezone, 7, 'day').startOf('day'),
    endDate: getStartDate(timezone, 1, 'day').endOf('day'),
    displayStartDate: getStartDate(timezone, 7, 'day').startOf('day'),
    displayEndDate: getStartDate(timezone, 1, 'day').endOf('day'),
  };
};

export const generateCompareCalendarProps = (timezone: string): ICalendarProps => {
  return {
    rangeSelected: 'Previous Period',
    rangeDisplaySelected: 'Compared To',
    startDate: getStartDate(timezone, 14, 'day').startOf('day'),
    endDate: getStartDate(timezone, 8, 'day').endOf('day'),
    displayStartDate: getStartDate(timezone, 14, 'day').startOf('day'),
    displayEndDate: getStartDate(timezone, 8, 'day').endOf('day'),
  };
};

interface IFindCompareDateArgs {
  timezone: string;
  startDate: moment.Moment;
  endDate: moment.Moment;
  rangeSelected: string;
}

export const findCompareStartDate = ({
  timezone,
  startDate,
  endDate,
  rangeSelected,
}: IFindCompareDateArgs): moment.Moment => {
  const calStartDate = cloneDeep(startDate);
  const calEndDate = cloneDeep(endDate);
  if (rangeSelected === THIS_WEEK_SUN_TODAY || rangeSelected === LAST_WEEK) {
    return moment(calStartDate).tz(timezone).subtract(1, 'week');
  } else if (rangeSelected === LAST_MONTH || rangeSelected === THIS_MONTH) {
    return moment(calStartDate).tz(timezone).subtract(1, 'month');
  } else {
    return moment(calStartDate)
      .tz(timezone)
      .subtract(1 + calEndDate.diff(calStartDate, 'days'), 'days')
      .startOf('day');
  }
};

export const findCompareEndDate = ({
  timezone,
  startDate,
  endDate,
  rangeSelected,
}: IFindCompareDateArgs): moment.Moment => {
  const calEndDate = cloneDeep(endDate);
  const calStartDate = cloneDeep(startDate);
  if (rangeSelected === THIS_WEEK_SUN_TODAY || rangeSelected === LAST_WEEK) {
    return moment(calEndDate).tz(timezone).subtract(1, 'week');
  } else if (rangeSelected === LAST_MONTH || rangeSelected === THIS_MONTH) {
    return moment(calEndDate).tz(timezone).subtract(1, 'month');
  } else {
    return moment(calStartDate).tz(timezone).subtract(1, 'days').endOf('day');
  }
};

export const generateCalendarQuickSelectorOptions = (
  timezone: string,
): ICalendarQuickSelectorOptions[] => {
  return [
    {
      title: YESTERDAY,
      date1: () => moment().tz(timezone).subtract(1, 'days'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
    {
      title: THIS_WEEK_SUN_YESTERDAY,
      date1: () => moment().tz(timezone).subtract(moment().tz(timezone).format('d'), 'days'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
    {
      title: LAST_7_DAYS,
      date1: () => moment().tz(timezone).subtract(7, 'days'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
    {
      title: LAST_WEEK,
      date1: () =>
        moment()
          .tz(timezone)
          .subtract(parseInt(moment().tz(timezone).format('d'), 10) + 7, 'days'),
      date2: () =>
        moment()
          .tz(timezone)
          .subtract(parseInt(moment().tz(timezone).format('d'), 10) + 1, 'days'),
    },
    {
      title: THIS_MONTH,
      date1: () => moment().tz(timezone).startOf('month'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
    {
      title: LAST_30_DAYS,
      date1: () => moment().tz(timezone).subtract(30, 'days'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
    {
      title: LAST_MONTH,
      date1: () => moment().tz(timezone).subtract(1, 'months').date(1),
      date2: () =>
        moment()
          .tz(timezone)
          .subtract(1, 'months')
          .date(moment().tz(timezone).subtract(1, 'months').daysInMonth()),
    },
  ];
};

export const generateCalendarQuickSelectorCustomOptions = (
  timezone: string,
): ICalendarQuickSelectorOptions[] => {
  return [
    {
      title: ' Days Up To Yesterday',
      date1: (e: number | string) => moment().tz(timezone).subtract(e, 'days'),
      date2: () => moment().tz(timezone).subtract(1, 'days'),
    },
  ];
};

const extractDeviceTypes = (list: IListItem[]) => {
  const deviceTypes = [] as string[];
  const clonedList = list.slice(0);
  const filteredList = clonedList.filter((item) => {
    return item.selected === true;
  });
  if (filteredList.length > 0) {
    filteredList.map((item) => {
      deviceTypes.push(...(item.values || []));
    });
  } else {
    list.map((item) => {
      deviceTypes.push(...(item.values || []));
    });
  }
  return deviceTypes;
};

export const extractDeviceTypesFromFilter = (filter: IMeasurementFilter) => {
  return extractDeviceTypes(Object.values(filter.list));
};

export const combineDeviceTypeFilters = (filters: Record<string, IMeasurementFilter>) => {
  const combinedDeviceTypes = [] as number[];
  const deviceTypes = [] as string[];
  Object.values(filters).forEach((filter) => {
    deviceTypes.push(...extractDeviceTypesFromFilter(filter));
  });
  const counter = {} as { [key: string]: number };
  deviceTypes.forEach((deviceType) => {
    counter[deviceType] = 1 + (counter[deviceType] || 0);
  });
  Object.entries(counter).forEach((item) => {
    if (item[1] > 1) {
      combinedDeviceTypes.push(+item[0]);
    }
  });
  return combinedDeviceTypes;
};

export const extractDataKeysFromColumns = (columns: IMeasurementTableColumn[]) => {
  const dataKeys: string[] = [];
  columns.forEach((col) => {
    dataKeys.push(col.dataKey);
  });
  return dataKeys;
};

const generateGroupByName = (name: string | undefined, id: string | undefined) => {
  if (name && id) {
    return `${name} (${id})`;
  } else if (name) {
    return `${name} (No ID)`;
  } else if (id) {
    return `(${id})`;
  } else {
    return `Uncategorized`;
  }
};

export const generateListTableData = (
  groupBy: IMeasurementTableGroupBy,
  columnKeys: string[],
  data: any,
) => {
  const listTableData: IMeasurementTableRowData[] = [];
  data.forEach((item: Record<string, any>) => {
    const row: Record<string, any> = {
      ID: item.ID,
      Name: generateGroupByName(item[groupBy.dataKey], item.ID),
      [groupBy.dataKey]: item[groupBy.dataKey],
    };
    columnKeys.forEach((key) => {
      row[key] = item[key];
    });
    listTableData.push(row as IMeasurementTableRowData);
  });
  return listTableData;
};

export const generateListTableColumns = (
  groupBy: IMeasurementTableGroupBy,
  columns: IMeasurementTableColumn[],
) => {
  // TODO: refactor node levels
  const listTableColumns: IListTableColumn[] = [
    {
      field: 'Name',
      title: groupBy.label,
      renderType:
        groupBy.label === 'Creatives' || groupBy.label === 'Ad Units'
          ? 'normal'
          : 'namelink_returnsnameid',
      searchable: true,
      minWidth: '320px',
      textAlignHeader: 'left',
      paddingLeft: groupBy.label === 'Creatives' || groupBy.label === 'Ad Units' ? '6px' : '14px',
    },
  ];
  columns.forEach((col) => {
    listTableColumns.push({
      field: col.dataKey,
      title: col.label,
      renderType: col.renderType,
    });
  });
  return listTableColumns;
};

export const generateItemsFromGroupBys = (groupBys: IMeasurementTableGroupBy[]) => {
  const items: ISingleSelectOption[] = [];
  groupBys.forEach((groupBy) => {
    items.push({
      label: groupBy.label,
      value: groupBy.dataKey,
    });
  });
  return items;
};

export const filterGroupBy = (
  keyString: string,
  keyValue: string,
  groupBys: IMeasurementTableGroupBy[],
) => {
  // @ts-ignore
  const result = groupBys.filter((item) => item[keyString] === keyValue);
  return result.length > 0 ? result[0] : groupBys[0];
};

export const isAnyFilterActive = (filters: IMeasurementFilter[]): boolean => {
  for (let i = 0; i < filters.length; i++) {
    const list = Object.values(filters[i].list);
    for (let j = 0; j < list.length; j++) {
      if (list[j].selected) {
        return true;
      }
    }
  }
  return false;
};
